import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Zap, Send, CheckCircle, AlertCircle, FileText, X,
    ChevronDown, Phone,
    Loader2, Wrench, List, Calculator,
    CheckCircle2, CreditCard, ShieldCheck, Info, Ticket, RotateCcw,
    Paperclip, DollarSign, BarChart2, Clock
} from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useSalesData } from '../context/SalesDataContext';
import { useTramite } from '../context/TramiteContext';
import PriceAppendix from './PriceAppendix';
import CallTraceAppendix from './CallTraceAppendix';
import LocalInvoiceManager from './LocalInvoiceManager';
import ZohoDeskTicketsAppendix from './ZohoDeskTicketsAppendix';
import CapabilityGate from './common/CapabilityGate';
import { useCapabilities } from '../hooks/useCapabilities';
import CopyablePhone from './CopyablePhone';

const TIPO_VIA_OPTIONS = [
    'Calle', 'Avenida', 'Plaza', 'Carretera', 'Paseo', 'Ronda', 'Travesía', 'Pasaje', 'Camino',
    'Urbanización', 'Acceso', 'Afueras', 'Agrupación', 'Alameda', 'Aldea', 'Arrabal', 'Autopista / Autovía',
    'Barranco', 'Barriada', 'Barrio', 'Bloque', 'Calleja', 'Callejón', 'Carril', 'Casa', 'Chalet', 'Colonia',
    'Complejo', 'Cooperativa', 'Cuesta', 'Diseminado extrarradio', 'Edificio', 'Entrada', 'Ficticio', 'Finca',
    'Glorieta', 'Grupo', 'Lugar', 'Manzana', 'Masía', 'Muelle', 'Núcleo', 'Otros', 'Pantalan', 'Paraje',
    'Parque', 'Partida', 'Playa', 'Plazoleta', 'Poblado', 'Políg.industrial', 'Polígono', 'Prolongación',
    'Rambla', 'Residencial', 'Senda', 'Vial', 'Zona'
];

const PROVINCIAS = [
    'Albacete', 'Alicante/Alacant', 'Almería', 'Araba/Álava', 'Asturias', 'Ávila', 'Badajoz', 'Balears, Illes',
    'Barcelona', 'Bizkaia', 'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón/Castelló', 'Ceuta',
    'Ciudad Real', 'Córdoba', 'Coruña, A', 'Cuenca', 'Gipuzkoa', 'Girona', 'Granada', 'Guadalajara',
    'Huelva', 'Huesca', 'Jaén', 'León', 'Lleida', 'Lugo', 'Madrid', 'Málaga', 'Melilla', 'Murcia',
    'Navarra', 'Otro', 'Ourense', 'Palencia', 'Palmas, Las', 'Pontevedra', 'Rioja, La', 'Salamanca',
    'Santa Cruz de Tenerife', 'Segovia', 'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo',
    'Valencia/València', 'Valladolid', 'Zamora', 'Zaragoza'
];


/**
 * Searchable Selector / Combobox (Autocomplete)
 */
const SearchableSelector = ({ value, onChange, placeholder, className, options = [], required, minLength = 2 }) => {
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Filtered options based on search term
    const filtered = searchTerm.length >= minLength
        ? options.filter(o => o.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 50)
        : [];

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (name) => {
        setSearchTerm(name);
        onChange(name);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={containerRef}>
            <input
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                autoComplete="off"
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsOpen(true);
                    if (e.target.value === '') onChange('');
                }}
                onFocus={() => setIsOpen(true)}
                className={`${className} pr - 8`}
                required={required}
            />
            {isOpen && filtered.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                    {filtered.map((m, idx) => (
                        <div
                            key={idx}
                            className="px-3 py-2 text-sm text-slate-200 hover:bg-primary hover:text-white cursor-pointer transition-colors border-b border-slate-800 last:border-0"
                            onClick={() => handleSelect(m)}
                        >
                            {m}
                        </div>
                    ))}
                </div>
            )}
            {searchTerm && (
                <button
                    type="button"
                    onClick={() => { setSearchTerm(''); onChange(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                    <X size={14} />
                </button>
            )}
        </div>
    );
};

/**
* Debounced Input with Sync Locking
* Defined OUTSIDE of SalesForm to prevent unmounting on parent re-render
*/
const DebouncedInput = ({ value: externalValue, onChange, name, className, ...props }) => {
    const [localValue, setLocalValue] = useState(externalValue || '');
    const [isFocused, setIsFocused] = useState(false);
    const lastPushedTimeRef = useRef(0);

    useEffect(() => {
        const now = Date.now();
        const isRecentlyChanged = now - lastPushedTimeRef.current < 2000;
        if (!isFocused && !isRecentlyChanged) {
            setLocalValue(externalValue || '');
        }
    }, [externalValue, isFocused]);

    const flushChange = useCallback(() => {
        if (localValue !== (externalValue || '')) {
            lastPushedTimeRef.current = Date.now();
            onChange({ target: { name, value: localValue, type: 'text' } });
        }
    }, [localValue, onChange, externalValue, name]);

    useEffect(() => {
        const timer = setTimeout(flushChange, 500);
        return () => clearTimeout(timer);
    }, [flushChange]);

    const handleBlur = (e) => {
        setIsFocused(false);
        flushChange();
        if (props.onBlur) props.onBlur(e);
    };

    return (
        <input
            {...props}
            name={name}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            className={className}
        />
    );
};

/**
 * Checkbox with Sync Locking
 * Defined OUTSIDE of SalesForm to prevent unmounting on parent re-render
 */
const SyncLockedCheckbox = ({ label, name, checked, onChange, className, isHighlighted, ...props }) => {
    const lastPushedTimeRef = useRef(0);
    const [localChecked, setLocalChecked] = useState(checked);

    useEffect(() => {
        const now = Date.now();
        if (now - lastPushedTimeRef.current >= 2000) {
            setLocalChecked(checked);
        }
    }, [checked]);

    const handleToggle = () => {
        const nextValue = !localChecked;
        setLocalChecked(nextValue);
        lastPushedTimeRef.current = Date.now();
        onChange({
            target: {
                name,
                type: 'checkbox',
                checked: nextValue
            }
        });
    };

    return (
        <label className={`flex items-center gap-3 text-xs text-slate-400 cursor-pointer group p-3 bg-slate-900/30 rounded-2xl border border-slate-700/50 hover:bg-slate-800 transition-all ${isHighlighted ? 'whatsapp-highlight !border-emerald-500/50' : ''} ${className || ''}`}>
            <div className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all ${localChecked ? 'bg-primary border-primary shadow-lg shadow-primary/20' : 'bg-slate-900 border-slate-700 group-hover:border-slate-500'}`}>
                {localChecked && <CheckCircle size={14} className="text-white" />}
                <input
                    type="checkbox"
                    className="hidden"
                    name={name}
                    checked={localChecked}
                    onChange={handleToggle}
                    {...props}
                />
            </div>
            <span className="font-bold uppercase tracking-tighter truncate">{label}</span>
        </label>
    );
};

export const validateField = (fieldName, value, clientType) => {
    if (!value) return true;
    switch (fieldName) {
        case 'cups':
            return /^ES[A-Z0-9]{18,22}$/.test(value.trim().toUpperCase());
        case 'nif':
            const doc = value.trim().toUpperCase();
            const regexDNI = /^[0-9]{8}[A-Z]$/;
            const regexNIE = /^[XYZ][0-9]{7}[A-Z]$/;
            const regexCIF = /^[A-Z][0-9]{7}[A-Z0-9]$/;
            if (clientType === 'residencial') {
                return regexDNI.test(doc) || regexNIE.test(doc);
            } else {
                return regexCIF.test(doc);
            }
        case 'email':
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
        case 'phone':
            const cleanPhone = value.replace(/[\s-]/g, '');
            return /^(?:\+34|0034)?[6-9]\d{8}$/.test(cleanPhone);
        case 'postal_code':
        case 'postal_code_suministro':
            return /^\d{5}$/.test(value.trim());
        case 'iban':
            const cleanIban = value.replace(/[\s-]/g, '').toUpperCase();
            return /^ES\d{22}$/.test(cleanIban);
        case 'fecha_inicio':
            if (!value) return true;
            const selDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return selDate >= today;
        default:
            return true;
    }
};

const SalesForm = ({ contactId, authFetch: propAuthFetch, setOpenedWaConversationId, onOpenHistoricalConversation, onOpenCallTrace, setIsWaInboxOpen, openedWaConversationId, pendingWaMessageId, setPendingWaMessageId, forceOpenAttachmentsForConvId, onAttachmentsOpened }) => {
    const { authFetch, agentInfo } = useAuth();
    const {
        formStatus,
        setFormStatus,
        sectionData,
        updateSectionData,
        fieldSources,
        offers,
        priceData,
        setPriceData,
        extractedData,
        setExtractedData,
        analyzedAttachments,
        setAnalyzedAttachments,
        scoringError,
        setScoringError,
        comparatorError,
        setComparatorError,
        tarifas,
        loadingTarifas,
        loadTarifas
    } = useSalesData();
    const { hasCapability } = useCapabilities();
    const canSeeOfferDetails = hasCapability('offers');
    const { currentTramite } = useTramite();
    const [analyzingFileId, setAnalyzingFileId] = useState(null);

    /**
     * Internal components that use fieldSources closure
     */




    const hasPermission = React.useCallback((perm) => {
        if (!agentInfo?.permissions) return false;
        if (Array.isArray(agentInfo.permissions)) return agentInfo.permissions.includes(perm);
        if (agentInfo.permissions.capabilities && Array.isArray(agentInfo.permissions.capabilities)) {
            return agentInfo.permissions.capabilities.includes(perm);
        }
        return false;
    }, [agentInfo]);

    // Use useMemo to prevent formData from being recreated on every render
    const formData = React.useMemo(() => ({
        client_type: 'residencial',
        cups: '',
        full_name: '',
        nif: '',
        phone: '',
        email: '',
        address_full: '',
        aclarador_finca: '',
        province: '',
        town: '',
        postal_code: '',
        // Supply address fields
        direccion_suministro_distinta: false,
        address_suministro_full: '',
        aclarador_finca_suministro: '',
        province_suministro: '',
        town_suministro: '',
        postal_code_suministro: '',
        tipo_via_cnmc: 'Calle',
        tipo_via_titular_cnmc: 'Calle',
        es_alta_nueva: false,
        mismo_titular: true,
        misma_potencia: true,
        no_credit_check: false,
        tipo_autoconsumo_cnmc: '',
        inicio_contrato: 'cuanto_antes',
        fecha_inicio: '',
        iban: '',
        is_autonomo: false,
        tarifa_id: '',
        canal_envio: 'email',
        mandar_a_firma: true,
        fecha_firma_datos: new Date().toISOString().split('T')[0],
        contract_code: '',
        contract_status: '',
        firma_status: '',
        ...sectionData?.SalesForm,
        ...(!hasPermission('no_mandar_firma') ? { mandar_a_firma: true } : {}),
        ...(!hasPermission('no_credit_check') ? { no_credit_check: false } : {})
    }), [sectionData?.SalesForm, hasPermission]);



    // Use a ref to track the latest formData without making handleSaveDraft dependent on it
    const formDataRef = useRef(formData);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    // Flag: only true when the USER made a change (not when Realtime updates the state)
    // This prevents the auto-save from looping with Realtime updates (Level 2 in patron_inputs_debounced.md)
    const isDirtyRef = useRef(false);

    const [showTarifas, setShowTarifas] = useState(false);
    const [selectedTarifa, setSelectedTarifa] = useState(null);
    const [scoringStatus, setScoringStatus] = useState('IDLE'); // IDLE, PENDING, APPROVED, DENIED, ERROR, TIMEOUT
    const [isScoring, setIsScoring] = useState(false);

    useEffect(() => {
        let timer;
        if (scoringStatus === 'PENDING') {
            timer = setTimeout(() => {
                setScoringStatus('TIMEOUT');
            }, 60000); // 1 minuto
        }
        return () => clearTimeout(timer);
    }, [scoringStatus]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('contact');
    const [isAppendixOpen, setIsAppendixOpen] = useState(false);
    const lastFetchedRef = useRef({ cups: '', tarifa_id: '' });


    // WhatsApp / Tools State
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

    const [municipios, setMunicipios] = useState([]);
    const [highlightMissingScoring, setHighlightMissingScoring] = useState(false);
    const [highlightMissingFields, setHighlightMissingFields] = useState(false);
    const [highlightMissingComparison, setHighlightMissingComparison] = useState(false);
    const [isZohoTicketsOpen, setIsZohoTicketsOpen] = useState(false);

    const isFormValid = React.useMemo(() => {
        // 1. Mandatory base fields
        const mandatoryBase = [
            'cups', 'full_name', 'nif', 'phone', 'email',
            'address_full', 'province', 'town', 'postal_code',
            'iban', 'tarifa_id'
        ];

        // 2. Address supply if different
        const mandatorySuministro = formData.direccion_suministro_distinta
            ? ['address_suministro_full', 'province_suministro', 'town_suministro', 'postal_code_suministro']
            : [];

        // 3. Potencies
        // User rule: only P1-P6 are mandatory if NOT 2.0TD. If 2.0TD, only P1-P2.
        const is20TD = selectedTarifa?.descripcion === '2.0TD';
        const mandatoryPotencies = is20TD ? ['potencia_p1', 'potencia_p2'] : ['potencia_p1', 'potencia_p2', 'potencia_p3', 'potencia_p4', 'potencia_p5', 'potencia_p6'];

        // 4. Date
        const mandatoryDate = formData.inicio_contrato === 'fecha_fija' ? ['fecha_inicio'] : [];

        const allMandatoryFields = [...mandatoryBase, ...mandatorySuministro, ...mandatoryPotencies, ...mandatoryDate];

        // Check if all are filled
        const allFilled = allMandatoryFields.every(key => !!formData[key]);
        if (!allFilled) return false;

        // Check if all are valid
        const allValid = allMandatoryFields.every(key => validateField(key, formData[key], formData.client_type));
        if (!allValid) return false;

        // Scoring requirement
        if (scoringStatus !== 'APPROVED' && scoringStatus !== 'TIMEOUT') return false;

        return true;
    }, [formData, selectedTarifa, scoringStatus]);

    const isComparisonValid = React.useMemo(() => {
        const is20TD = selectedTarifa ? selectedTarifa.descripcion === '2.0TD' : formData.client_type === 'residencial';
        const mandatory = is20TD ? ['p1', 'p2'] : ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];
        
        if (!formData.cups || !formData.tarifa_id || !priceData.imagina) return false;
        
        // Validar que tenemos CUPS validos
        if (!validateField('cups', formData.cups, formData.client_type)) return false;

        // Validar que tenemos consumos y potencias (para calcular el PDF)
        const hasConsumos = mandatory.every(p => !!formData[p] && !!formData[`potencia_${p}`]);
        if (!hasConsumos) return false;

        // Validar que tenemos los precios del proveedor actual
        const hasPrices = mandatory.every(p => !!formData[`current_price_energy_${p}`] && !!formData[`current_price_power_${p}`]);
        if (!hasPrices) return false;

        return true;
    }, [formData, selectedTarifa, priceData.imagina]);

    // Debug: log reasons why comparison is not valid
    useEffect(() => {
        if (!isComparisonValid) {
            const reasons = [];
            if (!formData.cups) reasons.push('Missing CUPS');
            else if (!validateField('cups', formData.cups, formData.client_type)) reasons.push('Invalid CUPS');
            
            if (!formData.tarifa_id) reasons.push('Missing Tarifa');
            if (!priceData.imagina) reasons.push('Missing Imagina Price (Calculator not clicked?)');

            const is20TD = selectedTarifa ? selectedTarifa.descripcion === '2.0TD' : formData.client_type === 'residencial';
            const mandatory = is20TD ? ['p1', 'p2'] : ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];

            mandatory.forEach(p => {
                if (!formData[p]) reasons.push(`Missing Consumption ${p.toUpperCase()}`);
                if (!formData[`potencia_${p}`]) reasons.push(`Missing Potency ${p.toUpperCase()}`);
                if (!formData[`current_price_energy_${p}`]) reasons.push(`Missing Current Energy Price ${p.toUpperCase()}`);
                if (!formData[`current_price_power_${p}`]) reasons.push(`Missing Current Power Price ${p.toUpperCase()}`);
            });

            if (reasons.length > 0) {
                console.log('[Comparison Debug] Valid: false. Reasons:', reasons);
            }
        } else {
            console.log('[Comparison Debug] Valid: true');
        }
    }, [isComparisonValid, formData, selectedTarifa, priceData.imagina]);

    // Fetch Municipios (List of 8200ish)
    useEffect(() => {
        const loadMunicipios = async () => {
            try {
                const response = await authFetch('/api/sale/municipios');
                if (response.ok) {
                    const data = await response.json();
                    setMunicipios(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error("Error loading municipios:", err);
            }
        };
        loadMunicipios();
    }, [authFetch]);

    // Load tariffs only when the user opens the tariff selector.
    useEffect(() => {
        if (showTarifas) loadTarifas();
    }, [showTarifas, loadTarifas]);

    // Offers are now provided globally by SalesDataContext

    // Derived: Filtered Tarifas based on Campaign Offers
    const filteredTarifas = React.useMemo(() => {
        // Filter tariffs by the identifiers in the global offers list
        return tarifas.filter(t =>
            offers.some(offer => String(offer.id_tarifa) === String(t.id_tarifa_precios))
        );
    }, [tarifas, offers]);

    // Sync selectedTarifa visual state with tarifa_id from database on load
    useEffect(() => {
        if (formData.tarifa_id && !selectedTarifa && filteredTarifas.length > 0) {
            const matching = filteredTarifas.find(t => String(t.id_tarifa_precios) === String(formData.tarifa_id));
            if (matching) {
                setSelectedTarifa(matching);
            }
        }
    }, [formData.tarifa_id, filteredTarifas, selectedTarifa]);

    useEffect(() => {
        setScoringStatus('IDLE');
        setScoringError(null);
    }, [contactId, formData.cups, formData.nif]);

    useEffect(() => {
        const { full_name, nif, cups, province, town, postal_code } = formData;
        const missing = [];
        if (!full_name) missing.push('full_name');
        if (!nif) missing.push('nif');
        if (!cups) missing.push('cups');
        if (!province) missing.push('province');
        if (!town) missing.push('town');
        if (!postal_code) missing.push('postal_code');

        if (scoringStatus === 'IDLE') {
            // console.log('[Scoring Debug] Status: IDLE. Missing fields:', missing.length > 0 ? missing.join(', ') : 'NONE');
        }
    }, [formData.full_name, formData.nif, formData.cups, formData.province, formData.town, formData.postal_code, scoringStatus]);

    const performScoring = useCallback(async () => {
        const { full_name, nif, cups, province, town, postal_code } = formData;
        if (!full_name || !nif || !cups || !province || !town || !postal_code) {
            return;
        }

        if (!validateField('nif', nif, formData.client_type) ||
            !validateField('cups', cups, formData.client_type) ||
            !validateField('postal_code', postal_code, formData.client_type)) {
            return;
        }

        setScoringStatus('PENDING');
        // Clear previous results to ensure Realtime triggers a change when new ones arrive
        updateSectionData('SalesForm', { scoring_result: null, scoring_detail: null });
        isDirtyRef.current = true; // Force persistence of cleared state
        // console.log('[Scoring] Iniciando scoring para:', { full_name, nif, cups, province, town });

        try {
            const res = await authFetch('/api/sale/creditcheck', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tramite_id: currentTramite?.id, // Primary identifier for referencia_externa
                    contact_id: contactId,
                    full_name,
                    nif,
                    client_type: formData.client_type,
                    is_autonomo: formData.is_autonomo,
                    address_full: formData.address_full,
                    postal_code,
                    cups,
                    town,
                    province
                })
            });

            if (res.ok) {
                const data = await res.json();
                // console.log('[Scoring] Respuesta:', data);

                // Normalización de resultado según result_operation o status_code interno
                const resultOp = (data.result_operation || '').toUpperCase();
                const intStatusCode = res.status; // Usar status de la respuesta HTTP real

                if (intStatusCode === 202) {
                    setScoringStatus('PENDING');
                    return;
                }

                let finalStatus = 'APPROVED';
                if (resultOp.includes('DENIED') || resultOp.includes('ERROR') || intStatusCode === 500) {
                    finalStatus = 'DENIED';
                } else if (resultOp.includes('APPROVED') || intStatusCode === 200) {
                    finalStatus = 'APPROVED';
                }

                setScoringStatus(finalStatus);
                if (finalStatus === 'DENIED') {
                    // setScoringError(data.result_operation || 'Scoring denergado');
                }

                // Auto-fill CAE and potencias from scoring API response
                const autoFill = {};
                if (data.potencia_p1 !== undefined) autoFill.potencia_p1 = String(data.potencia_p1);
                if (data.potencia_p2 !== undefined) autoFill.potencia_p2 = String(data.potencia_p2);
                if (data.potencia_p3 !== undefined) autoFill.potencia_p3 = String(data.potencia_p3);
                if (data.potencia_p4 !== undefined) autoFill.potencia_p4 = String(data.potencia_p4);
                if (data.potencia_p5 !== undefined) autoFill.potencia_p5 = String(data.potencia_p5);
                if (data.potencia_p6 !== undefined) autoFill.potencia_p6 = String(data.potencia_p6);
                if (data.cae !== undefined) autoFill.cae = String(data.cae);
                if (Object.keys(autoFill).length > 0) {
                    updateSectionData('SalesForm', autoFill);
                }
            } else {
                const errData = await res.json().catch(() => ({}));
                console.warn('[Scoring] Error HTTP:', res.status, errData);
                setScoringStatus('ERROR'); // Use ERROR, not IDLE, to prevent infinite re-trigger
            }
        } catch (err) {
            console.error('[Scoring] Fallo de conexión:', err);
            setScoringStatus('ERROR'); // Use ERROR, not IDLE, to prevent infinite re-trigger
        }
    }, [formData, contactId, authFetch, updateSectionData, currentTramite?.id]);

    // Effect to react to Realtime scoring updates from backend callback
    useEffect(() => {
        const result = formData.scoring_result;
        if (!result) return;

        console.log('[Scoring] Realtime update received:', result);

        if (result === 'APPROVED') {
            setScoringStatus('APPROVED');
            setScoringError(null);
        } else if (result === 'DENIED') {
            setScoringStatus('DENIED');
        }
    }, [formData.scoring_result, formData.scoring_detail, scoringStatus]);
    
    // Effect to react to Realtime contract callback updates from backend
    useEffect(() => {
        if (formStatus === 'ERROR' && formData.submit_error) {
            console.log('[ContractCallback] Async error received:', formData.submit_error);
            setComparatorError(`Error en el procesamiento del contrato:\n\n${formData.submit_error}`);
        }
    }, [formData.submit_error, formStatus, setComparatorError]);

    // Comparator Logic: Fetch Imagina Price
    const fetchImaginaPrice = useCallback(async (eOrForce) => {
        const force = (eOrForce === true) || (eOrForce && eOrForce.type === 'click');
        const { cups, tarifa_id } = formData;
        if (!cups || !tarifa_id) return;

        // Fetch guard: avoid redundant calls for same cups + tarifa or if already loading
        if (!force && ((lastFetchedRef.current.cups === cups && lastFetchedRef.current.tarifa_id === tarifa_id) || priceData.loadingImagina)) {
            // console.log('[PriceAppendix] Skipping redundant fetch');
            return;
        }

        lastFetchedRef.current = { cups, tarifa_id };
        setPriceData(prev => ({ ...prev, loadingImagina: true }));
        setIsAppendixOpen(true);

        try {
            const response = await authFetch('/api/sale/comparador', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cups, id_tarifa: tarifa_id })
            });
            if (response.ok) {
                const data = await response.json();
                setPriceData(prev => ({
                    ...prev,
                    imagina: {
                        coste_energia: data.coste_energia,
                        coste_potencia: data.coste_potencia,
                        total_anual: data.total
                    },
                    loadingImagina: false
                }));
                return data.total; // Useful for chaining
            } else {
                let errorMsg = 'Error en el comparador';
                try {
                    const errData = await response.json();
                    errorMsg = errData.error || errData.message || errData.details || JSON.stringify(errData);
                } catch (e) {
                    errorMsg = `Error ${response.status}: ${response.statusText}`;
                }
                setComparatorError(`Imposible calcular la propuesta de Imagina Energía: ${errorMsg}`);
                setPriceData(prev => ({ ...prev, loadingImagina: false }));
            }
        } catch (err) {
            console.error('[PriceAppendix] Imagina fetch failed:', err);
            setComparatorError('Error de red al intentar conectar con el comparador de Imagina.');
            setPriceData(prev => ({ ...prev, loadingImagina: false }));
        }
    }, [authFetch, priceData.imagina, formData, isComparisonValid, setComparatorError]);

    const handleUnifiedComparison = useCallback(async () => {
        if (!isComparisonValid) return;
        setPriceData(prev => ({ ...prev, loadingComparison: true }));
        setIsAppendixOpen(true);

        try {
            const is20TD = selectedTarifa?.descripcion === '2.0TD';
            const pKeys = is20TD ? ['p1', 'p2'] : ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];

            const energiaFormat = {};
            const potenciaFormat = {};

            pKeys.forEach(k => {
                energiaFormat[k] = parseFloat(String(formData[`current_price_energy_${k}`]).replace(',', '.'));
                potenciaFormat[k] = parseFloat(String(formData[`current_price_power_${k}`]).replace(',', '.'));
            });

            const manualPayload = {
                cups: formData.cups,
                tipo_precio: 'fijo',
                precios_energia: energiaFormat,
                precios_potencia: potenciaFormat,
                fee: null
            };

            const compRes = await authFetch('/api/sale/comparador_anual_manual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(manualPayload)
            });

            if (!compRes.ok) {
                let errorMsg = 'Error en la comparativa';
                try {
                    const errorData = await compRes.json();
                    errorMsg = errorData.error || errorData.message || errorData.details || JSON.stringify(errorData);
                } catch (e) {
                    errorMsg = `Error ${compRes.status}: ${compRes.statusText}`;
                }
                throw new Error(errorMsg);
            }
            
            const compData = await compRes.json();
            
            const safeParse = (val) => {
                if (val === null || val === undefined) return 0;
                if (typeof val === 'number') return val;
                const clean = String(val).replace(',', '.').replace(/[^\d.-]/g, '');
                const parsed = parseFloat(clean);
                return isNaN(parsed) ? 0 : parsed;
            };

            const totalEnergiaPdf = pKeys.reduce((acc, pKey) => {
                const consumption = safeParse(formData[pKey]);
                const price = safeParse(formData[`current_price_energy_${pKey}`]);
                return acc + (consumption * price);
            }, 0);

            const totalPotenciaPdf = pKeys.reduce((acc, pKey) => {
                const power = safeParse(formData[`potencia_${pKey}`]);
                const price = safeParse(formData[`current_price_power_${pKey}`]);
                return acc + (power * price * 30);
            }, 0);

            const totalFacturaPdf = totalEnergiaPdf + totalPotenciaPdf;
            
            const imagAnual = (priceData.imagina?.coste_energia || 0) + (priceData.imagina?.coste_potencia || 0);
            const totalImaginaMes = imagAnual > 0 ? (imagAnual / 12) : 0;
            const ahorroEstimadoMes = totalImaginaMes > 0 ? (totalFacturaPdf - totalImaginaMes) : 0;

            const comparisonResult = {
                ...compData,
                total_energia_pdf: totalEnergiaPdf,
                total_potencia_pdf: totalPotenciaPdf,
                total_factura_mes: totalFacturaPdf,
                total_imagina_mes: totalImaginaMes,
                ahorro_estimado_mes: ahorroEstimadoMes
            };

            setPriceData(prev => ({
                ...prev,
                comparison: comparisonResult,
                loadingComparison: false
            }));

        } catch (err) {
            console.error('Error in handleUnifiedComparison:', err);
            setComparatorError(err.message || 'Fallo la generación de la comparativa.');
            setPriceData(prev => ({ ...prev, loadingComparison: false }));
        }
    }, [isComparisonValid, formData, selectedTarifa, priceData.imagina, authFetch, setComparatorError]);

    // Automatically clear Imagina price if they edit CUPS or Tarifa to avoid showing stale calculations
    useEffect(() => {
        const { cups, tarifa_id } = formData;
        if (!cups || !tarifa_id) {
            const isAutomatedChange = fieldSources.cups === 'invoice' || fieldSources.cups === 'invoice_whatsapp';
            if (!isAutomatedChange) {
                setPriceData(prev => ({ ...prev, imagina: null, comparison: null }));
            }
        } else if (lastFetchedRef.current.cups !== '' && (cups !== lastFetchedRef.current.cups || tarifa_id !== lastFetchedRef.current.tarifa_id)) {
            setPriceData(prev => ({ ...prev, imagina: null, comparison: null }));
        }
    }, [formData.cups, formData.tarifa_id, fieldSources.cups]);

    // Restore extractedData from attachments on trámite change
    // This logic has been moved to SalesDataContext.js using pre-hydrated conversation metadata
    // to avoid redundant network requests.

    const handleWhatsAppFileAnalysis = useCallback(async (attachment) => {
        try {
            console.log('[WhatsApp] Preparing analysis for file:', attachment.id);
            setPriceData(prev => ({ ...prev, loadingComparison: false }));

            // 1. Download the file as blob
            const downloadRes = await authFetch(`/api/whatsapp/download/${attachment.id}`);
            if (!downloadRes.ok) throw new Error('Failed to download file');
            const blob = await downloadRes.blob();

            // 2. Create a File object
            const file = new File([blob], attachment.original_filename || 'whatsapp_invoice.pdf', { type: attachment.mime_type || 'application/pdf' });

            // 3. Extract Data directly here
            setAnalyzingFileId(attachment.id);
            const formDataExtractor = new FormData();
            formDataExtractor.append('file', file);

            const res = await authFetch('/api/sale/extract', {
                method: 'POST',
                body: formDataExtractor
            });

            if (!res.ok) throw new Error('Extraction failed');
            const data = await res.json();
            const extracted = (data.success && data.data) ? data.data : (data.data || data);

            if (extracted) {
                const getSection = (name) => {
                    if (extracted[name]) return extracted[name];
                    if (name === 'cliente' && (extracted.nombre_cliente || extracted.dni_cif)) return extracted;
                    if (name === 'contrato' && extracted.cups) return extracted;
                    if (name === 'consumo' && (extracted.consumo_p1 || extracted.consumo_p1_kWh)) return extracted;
                    if (name === 'potencia' && extracted.potencia_p1_kw) return extracted;
                    if (name === 'precios' && (extracted.precio_energia_p1 || extracted['precio_energia_p1_€'])) return extracted;
                    return {};
                };

                const cliente = getSection('cliente');
                const contrato = getSection('contrato');
                const potencia = getSection('potencia');
                const consumo = getSection('consumo');

                const updates = {};

                const getNested = (obj, keys) => {
                    for (const k of keys) {
                        if (obj && obj[k]) return obj[k];
                    }
                    return null;
                };

                const name = getNested(cliente, ['nombre_cliente', 'full_name', 'nombre_completo', 'titular', 'nombre']) ||
                    getNested(extracted, ['nombre_cliente', 'full_name', 'nombre_completo', 'titular', 'nombre']);
                if (name) updates.full_name = name;

                const id = getNested(cliente, ['dni_cif', 'nif', 'dni', 'cif', 'documento_identidad']) ||
                    getNested(extracted, ['dni_cif', 'nif', 'dni', 'cif', 'documento_identidad']);
                if (id) updates.nif = id;

                ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].forEach(p => {
                    const pVal = getNested(potencia, [`potencia_${p}_kw`, `potencia_${p}`, p, `${p}_kw`]) || 
                                 getNested(extracted, [`potencia_${p}_kw`, `potencia_${p}`]);
                    if (pVal) updates[`potencia_${p}`] = String(pVal);

                    // Map current prices from WhatsApp analysis
                    const pr = getSection('precios') || getSection('precio') || extracted;
                    
                    let priceEv = getNested(pr, [`precio_energia_${p}`, `energia_${p}`, `precio_energia_${p}_€`, `precio_energia_${p}_€/kWh`]);
                    if (!priceEv && pr.energia) priceEv = getNested(pr.energia, [p, `valor`, `precio`]);
                    if (!priceEv && extracted[`precio_energia_${p}`]) priceEv = extracted[`precio_energia_${p}`];
                    if (!priceEv && extracted[`precio_energia_${p}_€/kWh`]) priceEv = extracted[`precio_energia_${p}_€/kWh`];
                    if (priceEv) updates[`current_price_energy_${p}`] = String(priceEv);
                    
                    let pricePv = getNested(pr, [`precio_potencia_${p}`, `potencia_${p}`, `precio_potencia_${p}_€`, `precio_potencia_${p}_€/kW`]);
                    if (!pricePv && pr.potencia) pricePv = getNested(pr.potencia, [p, `valor`, `precio`]);
                    if (!pricePv && extracted[`precio_potencia_${p}`]) pricePv = extracted[`precio_potencia_${p}`];
                    if (!pricePv && extracted[`precio_potencia_${p}_€/kW`]) pricePv = extracted[`precio_potencia_${p}_€/kW`];
                    if (pricePv) updates[`current_price_power_${p}`] = String(pricePv);
                });

                const addr = getNested(cliente, ['direccion_suministro', 'address_full', 'direccion', 'domicilio']) ||
                    getNested(extracted, ['direccion_suministro', 'address_full', 'direccion', 'domicilio']);
                if (addr) updates.address_full = addr;

                const c = getNested(contrato, ['cups']) || getNested(extracted, ['cups']) || getNested(cliente, ['cups']);
                if (c) updates.cups = c;

                ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].forEach(p => {
                    const val = getNested(potencia, [`potencia_${p}_kw`, `potencia_${p}`, p, `${p}_kw`]) ||
                        getNested(extracted, [`potencia_${p}_kw`, `potencia_${p}`]);
                    if (val) updates[`potencia_${p}`] = String(val);
                });

                const caeVal = getNested(consumo, ['consumo_anual_estimado_kwh', 'cae', 'estimacion_anual']) ||
                    getNested(extracted, ['consumo_anual_estimado_kwh', 'cae']);
                if (caeVal) {
                    updates.cae = String(caeVal);
                }

                console.log('DEBUG [WhatsApp]: Mapped updates object:', updates);

                if (Object.keys(updates).length > 0) {
                    updateSectionData('SalesForm', updates, 'invoice_whatsapp');
                }

                setExtractedData(prev => ({ ...prev, [attachment.id]: extracted }));
            }

            // 4. Mark as analyzed
            if (attachment.id) {
                setAnalyzedAttachments(prev => new Set([...prev, attachment.id]));
            }

            console.log('[WhatsApp] File extraction applied to form from WA blob');
        } catch (err) {
            setComparatorError(err.message || 'Error al analizar la factura de WhatsApp. Por favor, inténtelo de nuevo.');
        } finally {
            setAnalyzingFileId(null);
        }
    }, [authFetch, setAnalyzingFileId, setAnalyzedAttachments, updateSectionData, setExtractedData]);

    const handleWhatsAppFileComparison = useCallback(async (attachment) => {
        try {
            let extracted = extractedData[attachment.id];
            setPriceData(prev => ({ ...prev, loadingComparison: true }));
            setIsAppendixOpen(true);

            if (!extracted) {
                console.log('[WhatsApp] Comparison clicked but no extraction data found yet, extracting...');
                const downloadRes = await authFetch(`/api/whatsapp/download/${attachment.id}`);
                if (!downloadRes.ok) throw new Error('Failed to download file');
                const blob = await downloadRes.blob();
                const file = new File([blob], attachment.original_filename || 'whatsapp_invoice.pdf', { type: attachment.mime_type || 'application/pdf' });

                const formDataExtractor = new FormData();
                formDataExtractor.append('file', file);
                const res = await authFetch('/api/sale/extract', { method: 'POST', body: formDataExtractor });
                if (!res.ok) {
                    let errorMsg = 'Error en la extracción';
                    try {
                        const errorData = await res.json();
                        errorMsg = errorData.error || errorData.message || errorData.details || JSON.stringify(errorData);
                    } catch (e) {
                        errorMsg = `Error ${res.status}: ${res.statusText}`;
                    }
                    throw new Error(errorMsg);
                }
                const data = await res.json();
                extracted = (data.success && data.data) ? data.data : (data.data || data);
                if (extracted) {
                    setExtractedData(prev => ({ ...prev, [attachment.id]: extracted }));
                }
            }

            if (!extracted) throw new Error("No extraction data generated");

            console.log('[WhatsApp] Comparing with extracted data for file:', attachment.id);

            const getSection = (name) => {
                if (extracted[name]) return extracted[name];
                if (name === 'contrato' && extracted.cups) return extracted;
                if (name === 'precios' && (extracted.precio_energia_p1 || extracted['precio_energia_p1_€'])) return extracted;
                return {};
            };

            const contrato = getSection('contrato');
            const precios = getSection('precios') || getSection('precio') || {};

            const energiaFormat = {};
            const potenciaFormat = {};
            const pKeys = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];

            pKeys.forEach(k => {
                if (precios.energia?.[k] !== undefined) energiaFormat[k] = precios.energia[k];
                else {
                    const ePrefix = `precio_energia_${k}`;
                    const actualEKey = Object.keys(precios).find(key => key.startsWith(ePrefix));
                    if (actualEKey) energiaFormat[k] = precios[actualEKey];
                }
                if (precios.potencia?.[k] !== undefined) potenciaFormat[k] = precios.potencia[k];
                else {
                    const pPrefix = `precio_potencia_${k}`;
                    const actualPKey = Object.keys(precios).find(key => key.startsWith(pPrefix));
                    if (actualPKey) potenciaFormat[k] = precios[actualPKey];
                }
            });

            const currentCups = formData.cups;
            const manualPayload = {
                cups: contrato.cups || currentCups,
                tipo_precio: contrato.tipo_precio || precios.tipo_precio || 'fijo',
                precios_energia: energiaFormat,
                precios_potencia: potenciaFormat,
                fee: precios.fee ?? precios.termino_fijo ?? null
            };

            const compRes = await authFetch('/api/sale/comparador_anual_manual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(manualPayload)
            });

            if (!compRes.ok) throw new Error('Comparison API failed');
            const compData = await compRes.json();

            const safeParse = (val) => {
                if (val === null || val === undefined) return 0;
                if (typeof val === 'number') return val;
                const clean = String(val).replace(',', '.').replace(/[^\d.-]/g, '');
                const parsed = parseFloat(clean);
                return isNaN(parsed) ? 0 : parsed;
            };

            const totales = extracted.totales || {};
            const consumo = extracted.consumo || {};
            const pr = extracted.precios || extracted.precio || {};
            const pt = extracted.potencia || {};

            let totalEnergiaPdf = safeParse(totales['total_energia_€'] || totales.total_energia || totales.subtotal_energia || totales.energia);
            let totalPotenciaPdf = safeParse(totales['total_potencia_€'] || totales.total_potencia || totales.subtotal_potencia || totales.potencia);

            if (totalEnergiaPdf === 0 && (Object.keys(consumo).length > 0 || Object.keys(extracted).some(k => k.includes('consumo')))) {
                const c = Object.keys(consumo).length > 0 ? consumo : extracted;
                const p = pr.energia || pr || extracted;
                totalEnergiaPdf = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].reduce((acc, pKey) => {
                    const cKey = Object.keys(c).find(k => k.toLowerCase().includes(`consumo_${pKey}`) || k.toLowerCase() === pKey);
                    const prKey = Object.keys(p).find(k => k.toLowerCase().includes(`energia_${pKey}`) || k.toLowerCase().includes(`precios_${pKey}`) || k.toLowerCase() === pKey);
                    const kwValue = cKey ? safeParse(c[cKey]) : 0;
                    const prValue = prKey ? safeParse(p[prKey]) : 0;
                    return acc + (kwValue * prValue);
                }, 0);
            }

            if (totalPotenciaPdf === 0 && (Object.keys(pt).length > 0 || Object.keys(extracted).some(k => k.includes('potencia')))) {
                const pow = Object.keys(pt).length > 0 ? pt : extracted;
                const p = pr.potencia || pr || extracted;
                const days = 30;
                totalPotenciaPdf = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].reduce((acc, pKey) => {
                    const powKey = Object.keys(pow).find(k => k.toLowerCase().includes(`potencia_${pKey}`) || k.toLowerCase() === pKey);
                    const prKey = Object.keys(p).find(k => k.toLowerCase().includes(`potencia_${pKey}`) || k.toLowerCase() === pKey);
                    const kwValue = powKey ? safeParse(pow[powKey]) : 0;
                    const prValue = prKey ? safeParse(p[prKey]) : 0;
                    return acc + (kwValue * prValue * days);
                }, 0);
            }

            const totalFacturaPdf = safeParse(totales['total_factura_€'] || totales.total_factura || totales.total || totales.importe_total) || (totalEnergiaPdf + totalPotenciaPdf);

            const pDataImagina = priceData.imagina;
            const imagAnual = (pDataImagina?.coste_energia || 0) + (pDataImagina?.coste_potencia || 0);
            const totalImaginaMes = imagAnual > 0 ? (imagAnual / 12) : 0;
            const ahorroEstimadoMes = totalImaginaMes > 0 ? (totalFacturaPdf - totalImaginaMes) : 0;

            const comparisonResult = {
                ...compData,
                total_energia_pdf: totalEnergiaPdf,
                total_potencia_pdf: totalPotenciaPdf,
                total_factura_mes: totalFacturaPdf,
                total_imagina_mes: totalImaginaMes,
                ahorro_estimado_mes: ahorroEstimadoMes
            };

            setPriceData(prev => ({ ...prev, comparison: comparisonResult, loadingComparison: false }));

        } catch (err) {
            console.error('Error in WhatsApp handleCompare:', err);
            setComparatorError('La extracción funcionó, pero falló la generación de la comparativa de la factura.');
            setPriceData(prev => ({ ...prev, loadingComparison: false }));
        }
    }, [authFetch, extractedData, setPriceData, setIsAppendixOpen, setExtractedData, formData.cups, priceData.imagina]);

    useEffect(() => {
        const handleAnalyze = (e) => {
            if (e.detail) handleWhatsAppFileAnalysis(e.detail);
        };
        const handleCompare = (e) => {
            if (e.detail) handleWhatsAppFileComparison(e.detail);
        };
        window.addEventListener('wa-analyze-file', handleAnalyze);
        window.addEventListener('wa-compare-file', handleCompare);
        return () => {
            window.removeEventListener('wa-analyze-file', handleAnalyze);
            window.removeEventListener('wa-compare-file', handleCompare);
        };
    }, [handleWhatsAppFileAnalysis, handleWhatsAppFileComparison]);

    useEffect(() => {
        const { full_name, nif, cups, province, town, postal_code } = formData;

        // Only trigger from IDLE state with all mandatory fields present and correctly formatted
        const canTrigger = !!full_name && !!nif && !!cups && !!province && !!town && !!postal_code
            && validateField('nif', nif, formData.client_type)
            && validateField('cups', cups, formData.client_type)
            && validateField('postal_code', postal_code, formData.client_type)
            && scoringStatus === 'IDLE';

        if (!canTrigger) return;

        const timeoutId = setTimeout(performScoring, 1000); // Debounce
        return () => clearTimeout(timeoutId);
    }, [formData, scoringStatus, performScoring]); // Fixed dependency

    const handleSaveDraft = useCallback(async () => {
        // Manual trigger to update backend (e.g. if user wants to force save)
        try {
            await authFetch('/api/sale/update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    contact_id: currentTramite?.contact_id,
                    tramite_id: currentTramite?.id,
                    data: formDataRef.current
                })
            });
        } catch (err) {
            console.error("Error saving draft:", err);
        }
    }, [currentTramite, authFetch]); // Stable dependencies


    // Auto-save (debounced) — only fires if isDirtyRef is true (user made a change)
    // Realtime updates change sectionData.SalesForm but don't set isDirtyRef → no echo loop
    useEffect(() => {
        if (!currentTramite?.id) return;
        if (!isDirtyRef.current) return; // Skip: this was a Realtime update, not a user change

        const saveTimeout = setTimeout(() => {
            if (isDirtyRef.current) {
                isDirtyRef.current = false; // Reset before saving to prevent double-save
                handleSaveDraft();
            }
        }, 1000);
        return () => clearTimeout(saveTimeout);
    }, [sectionData.SalesForm, currentTramite?.id, handleSaveDraft, formData]);




    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        isDirtyRef.current = true; // Mark as dirty: user explicitly changed a field
        updateSectionData('SalesForm', {
            [name]: type === 'checkbox' ? checked : value
        }, 'manual');
    }, [updateSectionData]);

    const handleSelectTarifa = (tarifa) => {
        setSelectedTarifa(tarifa);
        updateSectionData('SalesForm', { tarifa_id: tarifa.id_tarifa_precios }, 'manual');
        setShowTarifas(false);
    };

    const getInputClassName = (fieldName, extraClass = "") => {
        const baseClass = "w-full border border-slate-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary";
        const source = fieldSources[fieldName];
        let colorClass = "bg-slate-300 text-slate-900 placeholder-slate-500"; // Manual (Gris claro)

        if (source === 'zoho') {
            colorClass = "bg-sky-400 text-slate-900 placeholder-sky-700"; // Zoho (Azul claro)
        } else if (source === 'invoice') {
            colorClass = "bg-blue-900 text-white placeholder-blue-300 invoice-highlight"; // Factura Local (Azul oscuro + Glow)
        } else if (source === 'invoice_whatsapp') {
            colorClass = "bg-emerald-900 text-white placeholder-emerald-300 whatsapp-highlight"; // Factura WhatsApp (Verde oscuro + Glow)
        }

        const isValid = validateField(fieldName, formData[fieldName], formData.client_type);
        if (!isValid) {
            colorClass = "bg-red-500/10 border-red-500 text-red-100 placeholder-red-400 ring-2 ring-red-500/50";
        }

        // Highlight missing fields for scoring on hover
        const isRequiredForScoring = ['full_name', 'nif', 'cups', 'province', 'town', 'postal_code', 'address_full'].includes(fieldName);
        const isMissing = !formData[fieldName];
        if (highlightMissingScoring && isRequiredForScoring && isMissing) {
            colorClass = "bg-red-500/20 border-red-500 text-white placeholder-red-300 animate-pulse ring-2 ring-red-500/50";
        }

        // Highlight missing/invalid fields for Alta Contrato on hover
        const mandatoryBase = [
            'cups', 'full_name', 'nif', 'phone', 'email',
            'address_full', 'province', 'town', 'postal_code',
            'iban', 'tarifa_id'
        ];
        const is20TD = selectedTarifa?.descripcion === '2.0TD';
        const mandatoryPotencies = is20TD ? ['potencia_p1', 'potencia_p2'] : ['potencia_p1', 'potencia_p2', 'potencia_p3', 'potencia_p4', 'potencia_p5', 'potencia_p6'];
        const mandatoryDate = formData.inicio_contrato === 'fecha_fija' ? ['fecha_inicio'] : [];
        const mandatorySuministro = formData.direccion_suministro_distinta ? ['address_suministro_full', 'province_suministro', 'town_suministro', 'postal_code_suministro'] : [];

        const allMandatoryFieldsForAlta = [...mandatoryBase, ...mandatoryPotencies, ...mandatoryDate, ...mandatorySuministro];

        if (highlightMissingFields && allMandatoryFieldsForAlta.includes(fieldName)) {
            const isMissing = !formData[fieldName];
            const isValid = validateField(fieldName, formData[fieldName], formData.client_type);
            if (isMissing || !isValid) {
                colorClass = "bg-rose-500/20 border-rose-500 text-white placeholder-rose-300 animate-pulse ring-2 ring-red-500/50";
            }
        }

        // Highlight missing fields for Comparison on hover
        const mandatoryComparison = [
            'cups', 'tarifa_id',
            'p1', 'p2', 'p3', 'p4', 'p5', 'p6',
            'potencia_p1', 'potencia_p2', 'potencia_p3', 'potencia_p4', 'potencia_p5', 'potencia_p6',
            'current_price_energy_p1', 'current_price_energy_p2', 'current_price_energy_p3', 'current_price_energy_p4', 'current_price_energy_p5', 'current_price_energy_p6',
            'current_price_power_p1', 'current_price_power_p2', 'current_price_power_p3', 'current_price_power_p4', 'current_price_power_p5', 'current_price_power_p6'
        ];

        if (highlightMissingComparison && mandatoryComparison.includes(fieldName)) {
            const is20TDComp = selectedTarifa ? selectedTarifa.descripcion === '2.0TD' : formData.client_type === 'residencial';
            const mandatoryPeriods = is20TDComp ? ['p1', 'p2'] : ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];
            
            let isRelevant = false;
            if (['cups', 'tarifa_id'].includes(fieldName)) isRelevant = true;
            else {
                const period = ['p1','p2','p3','p4','p5','p6'].find(p => fieldName.endsWith(p));
                if (period && mandatoryPeriods.includes(period)) isRelevant = true;
            }

            if (isRelevant && !formData[fieldName]) {
                colorClass = "bg-blue-500/20 border-blue-500 text-white placeholder-blue-300 animate-pulse ring-2 ring-blue-500/50";
            }
        }

        return `${baseClass} ${extraClass} ${colorClass} transition-all duration-300`.trim();
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones previas al envío
        const fieldsToValidate = [
            { key: 'cups', msg: "El CUPS introducido no tiene un formato válido.\nDebe empezar por ES y tener entre 20 y 24 caracteres en total." },
            { key: 'nif', msg: formData.client_type === 'residencial' ? "Para clientes Residenciales, el identificador debe ser un DNI (8 números y 1 letra) o NIE válido." : "Para clientes tipo Empresa, el identificador debe ser un CIF válido." },
            { key: 'email', msg: "El correo electrónico introducido no tiene un formato válido." },
            { key: 'phone', msg: "El teléfono introducido no tiene un formato válido (debe ser un móvil o fijo español de 9 dígitos)." },
            { key: 'postal_code', msg: "El Código Postal de la dirección fiscal no tiene un formato válido (deben ser 5 números)." },
            { key: 'postal_code_suministro', msg: "El Código Postal de suministro no tiene un formato válido (deben ser 5 números)." },
            { key: 'iban', msg: "El IBAN introducido no tiene un formato válido.\nDebe empezar por ES y constar de 24 caracteres en total." }
        ];

        for (const field of fieldsToValidate) {
            if (formData[field.key] && !validateField(field.key, formData[field.key], formData.client_type)) {
                setComparatorError(field.msg);
                return;
            }
        }

        setLoading(true);

        try {
            const res = await authFetch('/api/sale/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contact_id: contactId,
                    tramite_id: currentTramite?.id,
                    data: formData
                })
            });

            // console.log('[Submit] Response status:', res.status);

            if (res.status === 200 || res.status === 202) {
                // Contrato enviado correctamente
                setFormStatus('ENVIADO');
            } else {
                setFormStatus('ERROR');
                // Intentar extraer el mensaje de error detallado del backend
                try {
                    const errData = await res.json();
                    const msg = errData.error || errData.message || (errData.details ? JSON.stringify(errData.details) : null) || "Error desconocido al procesar el alta.";
                    setComparatorError(`No se pudo completar el alta:\n\n${msg}`);
                } catch (e) {
                    setComparatorError("Error inesperado en el servidor al intentar procesar el alta.");
                }
            }
        } catch (err) {
            setFormStatus('ERROR');
            setComparatorError("Error de conexión al intentar enviar el contrato. Por favor, revisa tu conexión.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetForm = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setIsResetConfirmOpen(true);
    };

    const executeResetForm = () => {
        // Resetear a valores iniciales
        const emptyForm = {
            client_type: 'residencial',
            cups: '',
            full_name: '',
            nif: '',
            phone: '',
            email: '',
            address_full: '',
            aclarador_finca: '',
            province: '',
            town: '',
            postal_code: '',
            direccion_suministro_distinta: false,
            address_suministro_full: '',
            aclarador_finca_suministro: '',
            province_suministro: '',
            town_suministro: '',
            postal_code_suministro: '',
            tipo_via_cnmc: 'Calle',
            tipo_via_titular_cnmc: 'Calle',
            es_alta_nueva: false,
            mismo_titular: true,
            misma_potencia: true,
            no_credit_check: false,
            tipo_autoconsumo_cnmc: '',
            inicio_contrato: 'cuanto_antes',
            fecha_inicio: '',
            iban: '',
            is_autonomo: false,
            tarifa_id: '',
            p1: '', p2: '', p3: '', p4: '', p5: '', p6: '',
            potencia_p1: '', potencia_p2: '', potencia_p3: '', potencia_p4: '', potencia_p5: '', potencia_p6: '',
            current_price_energy_p1: '', current_price_energy_p2: '', current_price_energy_p3: '', 
            current_price_energy_p4: '', current_price_energy_p5: '', current_price_energy_p6: '',
            current_price_power_p1: '', current_price_power_p2: '', current_price_power_p3: '', 
            current_price_power_p4: '', current_price_power_p5: '', current_price_power_p6: '',
            cae: '',
            canal_envio: 'email',
            mandar_a_firma: true,
            fecha_firma_datos: new Date().toISOString().split('T')[0]
        };
        isDirtyRef.current = true; // Mark as dirty so auto-save clears the database
        updateSectionData('SalesForm', emptyForm, 'manual');

        // Clean local states
        setPriceData({
            imagina: null,
            comparison: null,
            loadingImagina: false,
            loadingComparison: false,
        });
        setScoringStatus('IDLE');
        setScoringError(null);
        setSelectedTarifa(null);
        setIsAppendixOpen(false);
        lastFetchedRef.current = { cups: '', tarifa_id: '' };
        setIsResetConfirmOpen(false);

        console.log('[SalesForm] Formulario y estados locales limpiados');
    };

    if (!currentTramite?.id) return null;

    return (
        <div className="w-full max-w-[1400px] mx-auto bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl flex flex-col transition-all duration-300 z-10 mb-20 overflow-hidden mt-8 animate-in fade-in zoom-in-95 duration-500">
            {/* Header: Always visible now, reduced padding */}
            <div className="flex items-center justify-between p-6 bg-slate-800/50 border-b border-slate-700">
                <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${formStatus === 'ENVIADO' ? 'bg-emerald-500/20' :
                        formStatus === 'ERROR' ? 'bg-red-500/20' :
                            'bg-primary/20'
                        }`}>
                        {formStatus === 'ENVIADO' ? <CheckCircle size={24} className="text-emerald-500" /> :
                            formStatus === 'ERROR' ? <AlertCircle size={24} className="text-red-400" /> :
                                <FileText size={24} className="text-primary" />}
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-slate-100 tracking-tight">Alta de Contrato de Energía</h2>
                        <div className="flex items-center gap-2 mt-1">
                            {formStatus === 'DRAFT' && <span className="text-[10px] px-2 py-0.5 bg-yellow-500/20 text-yellow-500 rounded-full font-bold uppercase tracking-widest border border-yellow-500/20">Borrador</span>}
                            {formStatus === 'ENVIADO' && !formData.contract_code && <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full font-bold uppercase tracking-widest border border-emerald-500/20 animate-pulse">Enviado (Procesando)</span>}
                            {formStatus === 'ENVIADO' && formData.contract_code && <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full font-bold uppercase tracking-widest border border-emerald-500/20">Alta Completada</span>}
                            {formStatus === 'ERROR' && <span className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full font-bold uppercase tracking-widest border border-red-500/20">Error</span>}
                            {formData.contract_code && (
                                <span className="text-[10px] px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full font-bold uppercase tracking-widest border border-blue-500/20 flex items-center gap-1">
                                    <FileText size={10} />
                                    CONTRATO: {formData.contract_code}
                                </span>
                            )}
                            <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest ml-1">TRÁMITE: {currentTramite?.id?.substring(0, 8)}</span>
                        </div>
                        {formStatus === 'ERROR' && formData.submit_error && (
                            <div className="mt-2 text-[11px] text-red-400 font-bold flex items-center gap-1.5 animate-in slide-in-from-left-2 duration-300">
                                <AlertCircle size={12} />
                                {formData.submit_error}
                            </div>
                        )}
                    </div>
                    {/* Reset Form Button */}
                    <button
                        type="button"
                        onClick={handleResetForm}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-xl text-slate-300 hover:text-slate-100 transition-all duration-200 text-sm font-medium group"
                        title="Limpiar todos los campos del formulario (si el cliente quiere una venta diferente)"
                    >
                        <RotateCcw size={16} className="group-hover:rotate-180 transition-transform duration-500" />
                        <span className="hidden sm:inline">Limpiar</span>
                    </button>
                </div>
            </div>

            {/* Body: Always visible, 3-column landscape grid */}
            <div className="p-8 bg-slate-900 border-t border-slate-700/50">
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                        {/* Column 1: Datos del Titular */}
                        <div className="space-y-6">
                            <div className="bg-slate-800/40 p-6 rounded-3xl border border-slate-700/50 hover:border-slate-600/50 transition-colors shadow-xl">
                                <div className="flex bg-slate-950/50 p-1 rounded-xl border border-slate-700/50 mb-6">
                                    <button
                                        type="button"
                                        onClick={() => updateSectionData('SalesForm', { client_type: 'residencial' })}
                                        className={`flex-1 py-1.5 text-[10px] font-black rounded-lg transition-all ${formData.client_type === 'residencial' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-slate-500 hover:text-slate-300'}`}
                                    >
                                        RESIDENCIAL
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateSectionData('SalesForm', { client_type: 'empresa' })}
                                        className={`flex-1 py-1.5 text-[10px] font-black rounded-lg transition-all ${formData.client_type === 'empresa' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-slate-500 hover:text-slate-300'}`}
                                    >
                                        EMPRESA
                                    </button>
                                </div>

                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-primary/10 rounded-xl">
                                        <List size={18} className="text-primary" />
                                    </div>
                                    <h3 className="text-sm font-black text-slate-100 uppercase tracking-tighter">Datos del Titular</h3>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    <DebouncedInput
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleChange}
                                        placeholder={formData.client_type === 'empresa' ? "Razón Social *" : "Nombre Completo *"}
                                        className={getInputClassName('full_name')}
                                        required
                                    />

                                    <div className="grid grid-cols-2 gap-3">
                                        <DebouncedInput
                                            name="nif"
                                            value={formData.nif}
                                            onChange={handleChange}
                                            placeholder={formData.client_type === 'empresa' ? "CIF *" : "DNI/NIE *"}
                                            className={getInputClassName('nif')}
                                            required
                                        />
                                        <div className="flex items-center gap-2">
                                            <div className="relative group flex-1">
                                                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-primary transition-colors" />
                                                <DebouncedInput
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="Teléfono"
                                                    className={getInputClassName('phone', 'pl-9')}
                                                />
                                            </div>
                                            {formData.phone && (
                                                <CopyablePhone
                                                    phone={formData.phone}
                                                    showText={false}
                                                    className="flex-shrink-0"
                                                    buttonClassName="p-2.5 bg-slate-800 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-700 rounded-xl"
                                                    searchButtonClassName="p-2.5 bg-slate-800 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-700 rounded-xl"
                                                />
                                            )}
                                            {hasPermission('calltrace') && formData.phone && (
                                                <button
                                                    type="button"
                                                    onClick={() => onOpenCallTrace(formData.phone ? [formData.phone] : [])}
                                                    className="p-2.5 bg-slate-800 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-700 rounded-xl transition-all text-slate-400 hover:text-blue-400 flex-shrink-0"
                                                    title="Ver histórico de este número"
                                                >
                                                    <Info size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <DebouncedInput
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Email corporativo / personal"
                                            className={getInputClassName('email', 'flex-1')}
                                        />
                                        {formData.email && (
                                            <CapabilityGate capability="ticketing">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsZohoTicketsOpen(true)}
                                                    className="p-2.5 bg-slate-800 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-700 rounded-xl transition-all text-slate-400 hover:text-blue-400 flex-shrink-0"
                                                    title="Ver tickets en Zoho Desk"
                                                >
                                                    <Ticket size={16} />
                                                </button>
                                            </CapabilityGate>
                                        )}
                                    </div>

                                    <DebouncedInput
                                        name="iban"
                                        value={formData.iban}
                                        onChange={handleChange}
                                        placeholder="IBAN (ES...)"
                                        className={getInputClassName('iban', 'font-mono uppercase')}
                                        required
                                    />

                                    <div className="pt-4 border-t border-slate-700/30 space-y-4">
                                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">Dirección Fiscal</p>
                                        <div className="flex gap-2">
                                            <select
                                                name="tipo_via_titular_cnmc"
                                                value={formData.tipo_via_titular_cnmc}
                                                onChange={handleChange}
                                                className="w-24 bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:ring-2 focus:ring-primary/50 outline-none"
                                            >
                                                {TIPO_VIA_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                            <DebouncedInput
                                                name="address_full"
                                                value={formData.address_full}
                                                onChange={handleChange}
                                                placeholder="Calle y número *"
                                                className={getInputClassName('address_full', 'flex-1')}
                                                required
                                            />
                                        </div>
                                        <DebouncedInput
                                            name="aclarador_finca"
                                            value={formData.aclarador_finca}
                                            onChange={handleChange}
                                            placeholder="Portal, piso, puerta..."
                                            className={getInputClassName('aclarador_finca')}
                                        />
                                        <div className="grid grid-cols-3 gap-3">
                                            <DebouncedInput
                                                name="postal_code"
                                                value={formData.postal_code}
                                                onChange={handleChange}
                                                placeholder="C.P. *"
                                                maxLength={5}
                                                className={getInputClassName('postal_code')}
                                                required
                                            />
                                            <div className="col-span-2">
                                                <SearchableSelector
                                                    value={formData.province}
                                                    options={PROVINCIAS}
                                                    onChange={(val) => handleChange({ target: { name: 'province', value: val } })}
                                                    placeholder="Provincia *"
                                                    className={getInputClassName('province')}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <SearchableSelector
                                            value={formData.town}
                                            options={municipios}
                                            onChange={(val) => handleChange({ target: { name: 'town', value: val } })}
                                            placeholder="Municipio *"
                                            className={getInputClassName('town')}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Punto de Suministro */}
                        <div className="space-y-6">
                            <div className="bg-slate-800/40 p-6 rounded-3xl border border-slate-700/50 hover:border-slate-600/50 transition-colors shadow-xl">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-emerald-500/10 rounded-xl">
                                        <Zap size={18} className="text-emerald-500" />
                                    </div>
                                    <h3 className="text-sm font-black text-slate-100 uppercase tracking-tighter">Punto de Suministro</h3>
                                </div>

                                <div className="space-y-4">
                                    <DebouncedInput
                                        name="cups"
                                        value={formData.cups}
                                        onChange={handleChange}
                                        placeholder="CUPS (ES...) *"
                                        className={getInputClassName('cups', 'font-mono uppercase tracking-widest')}
                                        required
                                    />

                                    <div className="grid grid-cols-2 gap-4">
                                        {['p1', 'p2'].map((p) => (
                                            <div key={p} className="space-y-2">
                                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight ml-1">Periodo {p.toUpperCase()}</p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <DebouncedInput
                                                        name={`potencia_${p}`}
                                                        value={formData[`potencia_${p}`]}
                                                        onChange={handleChange}
                                                        placeholder="Pot (kW)"
                                                        className={getInputClassName(`potencia_${p}`)}
                                                    />
                                                    <DebouncedInput
                                                        name={p}
                                                        value={formData[p]}
                                                        onChange={handleChange}
                                                        placeholder="Cons (kWh)"
                                                        className={getInputClassName(p)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {formData.client_type === 'empresa' && (
                                        <div className="grid grid-cols-2 gap-4 border-t border-slate-700/30 pt-4 mt-2">
                                            {['p3', 'p4', 'p5', 'p6'].map((p) => (
                                                <div key={p} className="space-y-2">
                                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight ml-1">Periodo {p.toUpperCase()}</p>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <DebouncedInput
                                                            name={`potencia_${p}`}
                                                            value={formData[`potencia_${p}`]}
                                                            onChange={handleChange}
                                                            placeholder="Pot (kW)"
                                                            className={getInputClassName(`potencia_${p}`)}
                                                        />
                                                        <DebouncedInput
                                                            name={p}
                                                            value={formData[p]}
                                                            onChange={handleChange}
                                                            placeholder="Cons (kWh)"
                                                            className={getInputClassName(p)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="pt-2">
                                        <SyncLockedCheckbox
                                            name="direccion_suministro_distinta"
                                            label="Direccion del punto suministro distinta a la del titular"
                                            checked={formData.direccion_suministro_distinta}
                                            onChange={handleChange}
                                            className="bg-slate-900/40"
                                            isHighlighted={fieldSources.direccion_suministro_distinta === 'invoice' || fieldSources.direccion_suministro_distinta === 'invoice_whatsapp'}
                                        />
                                    </div>

                                    {formData.direccion_suministro_distinta && (
                                        <div className="bg-primary/5 p-4 rounded-2xl border border-primary/20 space-y-4 animate-in slide-in-from-top-4 duration-300">
                                            <div className="flex gap-2">
                                                <select
                                                    name="tipo_via_cnmc"
                                                    value={formData.tipo_via_cnmc}
                                                    onChange={handleChange}
                                                    className="w-24 bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-300 outline-none focus:ring-2 focus:ring-primary/50"
                                                >
                                                    {TIPO_VIA_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                                <DebouncedInput
                                                    name="address_suministro_full"
                                                    value={formData.address_suministro_full}
                                                    onChange={handleChange}
                                                    placeholder="Calle suministro *"
                                                    className={getInputClassName('address_suministro_full', 'flex-1')}
                                                    required
                                                />
                                            </div>
                                            <div className="grid grid-cols-3 gap-3">
                                                <DebouncedInput
                                                    name="postal_code_suministro"
                                                    value={formData.postal_code_suministro}
                                                    onChange={handleChange}
                                                    placeholder="C.P. *"
                                                    maxLength={5}
                                                    className={getInputClassName('postal_code_suministro')}
                                                />
                                                <div className="col-span-2">
                                                    <SearchableSelector
                                                        value={formData.province_suministro}
                                                        options={PROVINCIAS}
                                                        onChange={(val) => handleChange({ target: { name: 'province_suministro', value: val } })}
                                                        placeholder="Provincia *"
                                                        className={getInputClassName('province_suministro')}
                                                    />
                                                </div>
                                            </div>
                                            <SearchableSelector
                                                value={formData.town_suministro}
                                                options={municipios}
                                                onChange={(val) => handleChange({ target: { name: 'town_suministro', value: val } })}
                                                placeholder="Municipio *"
                                                className={getInputClassName('town_suministro')}
                                            />
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-2 pt-2">
                                        {[
                                            { name: 'es_alta_nueva', label: 'Alta Nueva' },
                                            { name: 'mismo_titular', label: 'Mismo Titular' },
                                            { name: 'misma_potencia', label: 'Misma Potencia' },
                                            { name: 'no_credit_check', label: 'No Credit Check' },
                                            { name: 'direccion_suministro_distinta', label: 'Direccion del punto suministro distinta a la del titular' }
                                        ].filter(opt => {
                                            if (opt.name === 'no_credit_check') return hasPermission('no_credit_check');
                                            if (opt.name === 'es_alta_nueva') return hasPermission('A3');
                                            if (opt.name === 'mismo_titular' || opt.name === 'misma_potencia') return hasPermission('C2');
                                            if (opt.name === 'direccion_suministro_distinta') return false; // Handled separately in JSX above for layout reasons, but we add it here just to show we pass the prop if we re-factor
                                            return true;
                                        }).map((opt) => (
                                            <SyncLockedCheckbox
                                                key={opt.name}
                                                name={opt.name}
                                                label={opt.label}
                                                checked={formData[opt.name]}
                                                onChange={handleChange}
                                                isHighlighted={fieldSources[opt.name] === 'invoice' || fieldSources[opt.name] === 'invoice_whatsapp'}
                                            />
                                        ))}
                                    </div>

                                    {/* Sección de Precios Actuales */}
                                    <div className="pt-4 border-t border-slate-700/30 space-y-3">
                                        <div className="flex items-center gap-2">
                                            <DollarSign size={14} className="text-slate-500" />
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">Precios del Proveedor Actual</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                            {['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].map((p) => {
                                                if (p !== 'p1' && p !== 'p2' && formData.client_type !== 'empresa') return null;
                                                return (
                                                    <div key={p} className="space-y-1.5 p-2 bg-slate-900/30 rounded-xl border border-slate-700/30">
                                                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter ml-1">Periodo {p.toUpperCase()}</p>
                                                        <div className="flex gap-2">
                                                            <DebouncedInput
                                                                name={`current_price_energy_${p}`}
                                                                value={formData[`current_price_energy_${p}`]}
                                                                onChange={handleChange}
                                                                placeholder="En. (€/kWh)"
                                                                className={getInputClassName(`current_price_energy_${p}`, 'text-[11px] h-8')}
                                                            />
                                                            <DebouncedInput
                                                                name={`current_price_power_${p}`}
                                                                value={formData[`current_price_power_${p}`]}
                                                                onChange={handleChange}
                                                                placeholder="Pot. (€/kW/d)"
                                                                className={getInputClassName(`current_price_power_${p}`, 'text-[11px] h-8')}
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Column 3: Tarifa y Acciones */}
                        <div className="space-y-6">
                            {/* Herramientas de Captura */}
                            <div className="bg-slate-800/40 p-6 rounded-3xl border border-slate-700/50 shadow-xl space-y-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 bg-blue-500/10 rounded-xl">
                                        <Wrench size={18} className="text-blue-400" />
                                    </div>
                                    <h3 className="text-sm font-black text-slate-100 uppercase tracking-tighter">Herramientas</h3>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div
                                        onMouseEnter={() => scoringStatus === 'IDLE' && setHighlightMissingScoring(true)}
                                        onMouseLeave={() => setHighlightMissingScoring(false)}
                                        className={`flex flex-col items-center justify-center p-4 bg-slate-900/50 border border-slate-700/50 rounded-2xl transition-all group shadow-inner ${scoringStatus === 'APPROVED' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400' :
                                            scoringStatus === 'DENIED' ? 'border-red-500/50 bg-red-500/10 text-red-400' :
                                                (scoringStatus === 'ERROR' || scoringStatus === 'TIMEOUT') ? 'border-amber-500/50 bg-amber-500/10 text-amber-400' :
                                                    scoringStatus === 'PENDING' ? 'border-violet-500/50 bg-violet-500/10 text-violet-400' :
                                                        (highlightMissingFields && (scoringStatus === 'IDLE' || scoringStatus === 'DENIED' || scoringStatus === 'ERROR')) ? 'border-rose-500 bg-rose-500/20 animate-pulse ring-2 ring-rose-500/50 text-white' :
                                                            'hover:bg-slate-800 text-slate-400'
                                            }`}
                                    >
                                        {scoringStatus === 'PENDING' ? (
                                            <Loader2 size={24} className="mb-2 animate-spin text-violet-400" />
                                        ) : scoringStatus === 'APPROVED' ? (
                                            <CheckCircle2 size={24} className="mb-2" />
                                        ) : scoringStatus === 'DENIED' ? (
                                            <AlertCircle size={24} className="mb-2" />
                                        ) : (scoringStatus === 'ERROR' || scoringStatus === 'TIMEOUT') ? (
                                            <AlertCircle size={24} className="mb-2" />
                                        ) : (
                                            <ShieldCheck size={24} className="mb-2 transition-colors" />
                                        )}
                                        <span className={`text-[11px] font-bold uppercase tracking-wider text-center ${scoringStatus === 'APPROVED' ? 'text-emerald-400' :
                                            scoringStatus === 'DENIED' ? 'text-red-400' :
                                                (scoringStatus === 'ERROR' || scoringStatus === 'TIMEOUT') ? 'text-amber-400' :
                                                    scoringStatus === 'PENDING' ? 'text-violet-400' :
                                                        'text-slate-500'
                                            }`}>
                                            {scoringStatus === 'APPROVED' ? 'Riesgo OK' :
                                                scoringStatus === 'DENIED' ? 'Riesgo NO' :
                                                    scoringStatus === 'PENDING' ? 'Validando...' :
                                                        scoringStatus === 'TIMEOUT' ? 'NO LOGRADO' :
                                                            scoringStatus === 'ERROR' ? 'Error Scoring' :
                                                                'Scoring Pendiente'}
                                        </span>
                                    </div>
                                    <LocalInvoiceManager
                                        authFetch={authFetch}
                                        updateSectionData={updateSectionData}
                                        cups={formData.cups}
                                        imaginaData={priceData.imagina}
                                        onComparisonComplete={(comparisonResult) => {
                                            setPriceData(prev => ({ ...prev, comparison: comparisonResult }));
                                            setIsAppendixOpen(true);
                                        }}
                                    />
                                    <div className="flex-1" />
                                </div>
                            </div>

                            <div className="bg-slate-800/40 p-6 rounded-3xl border border-slate-700/50 hover:border-slate-600/50 transition-colors shadow-xl">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-primary/10 rounded-xl">
                                        <CreditCard size={18} className="text-primary" />
                                    </div>
                                    <h3 className="text-sm font-black text-slate-100 uppercase tracking-tighter">Selección de Tarifa</h3>
                                </div>

                                <div className="space-y-4">
                                    <div className="relative">
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setShowTarifas(!showTarifas)}
                                                className={`flex-1 bg-slate-900 border ${(highlightMissingFields || highlightMissingComparison) && !selectedTarifa ? 'border-rose-500 bg-rose-500/20 animate-pulse ring-2 ring-rose-500/50' : 'border-slate-700'} rounded-xl px-4 py-3 text-sm text-left flex items-center justify-between hover:border-primary/50 transition-all outline-none`}
                                            >
                                                <span className={selectedTarifa ? "text-slate-100 font-bold" : "text-slate-500"}>
                                                    {selectedTarifa 
                                                        ? `${selectedTarifa.nombre}${selectedTarifa.descripcion ? ` - ${selectedTarifa.descripcion}` : ''}` 
                                                        : "Seleccionar Tarifa *"
                                                    }
                                                </span>
                                                <ChevronDown size={18} className={`text-slate-500 transition-transform ${showTarifas ? 'rotate-180' : ''}`} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => fetchImaginaPrice(e)}
                                                disabled={!formData.cups || !validateField('cups', formData.cups, formData.client_type) || !formData.tarifa_id || priceData.loadingImagina}
                                                className={`px-4 bg-slate-900 border ${highlightMissingComparison && !priceData.imagina ? 'border-blue-500 bg-blue-500/10 animate-pulse ring-2 ring-blue-500/50' : 'border-slate-700'} rounded-xl text-slate-400 hover:text-primary hover:border-primary/50 transition-all disabled:opacity-20 flex items-center justify-center`}
                                                title="Calcular precio Imagina Energía"
                                            >
                                                <Calculator size={20} className={priceData.loadingImagina ? "animate-pulse" : ""} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleUnifiedComparison}
                                                onMouseEnter={() => !isComparisonValid && setHighlightMissingComparison(true)}
                                                onMouseLeave={() => setHighlightMissingComparison(false)}
                                                className={`px-4 bg-slate-900 border rounded-xl transition-all flex items-center justify-center ${
                                                    isComparisonValid 
                                                    ? 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10' 
                                                    : 'border-slate-700 text-slate-500 opacity-50 cursor-not-allowed'
                                                }`}
                                                title="Ver comparativa personalizada"
                                            >
                                                <BarChart2 size={18} />
                                            </button>
                                        </div>

                                        {showTarifas && (
                                            <div className="absolute z-[100] bottom-full left-0 right-0 mb-2 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
                                                <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-2">
                                                    {loadingTarifas && (
                                                        <div className="p-3 text-sm text-slate-400">Cargando tarifas...</div>
                                                    )}
                                                    {!loadingTarifas && filteredTarifas.length === 0 && (
                                                        <div className="p-3 text-sm text-slate-400">No hay tarifas disponibles.</div>
                                                    )}
                                                    {filteredTarifas.map((t) => {
                                                        const matchingOffer = offers.find(o => String(o.id_tarifa) === String(t.id_tarifa_precios));
                                                        return (
                                                            <div
                                                                key={t.id_tarifa_precios}
                                                                onClick={() => handleSelectTarifa(t)}
                                                                className={`p-3 rounded-xl mb-1 cursor-pointer transition-all ${selectedTarifa?.id_tarifa_precios === t.id_tarifa_precios ? 'bg-primary/20 border border-primary/40' : 'hover:bg-slate-800 border border-transparent'}`}
                                                            >
                                                                <div className="flex flex-col gap-1">
                                                                    <div className="flex justify-between items-baseline">
                                                                        <span className="text-sm font-black text-primary tracking-tighter">
                                                                            {canSeeOfferDetails ? (matchingOffer?.precio || '—') : '—'} <span className="text-[10px] uppercase opacity-60">€/kWh</span>
                                                                        </span>
                                                                        <span className="text-[9px] px-1.5 py-0.5 bg-slate-800 text-slate-500 rounded font-mono uppercase">
                                                                            {t.tipo} {t.descripcion && `| ${t.descripcion}`}
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-[11px] text-slate-200 font-bold leading-tight">
                                                                        {(canSeeOfferDetails && matchingOffer?.name) || t.nombre}
                                                                        {t.descripcion && <span className="ml-2 text-primary/80">({t.descripcion})</span>}
                                                                    </div>
                                                                    {canSeeOfferDetails && matchingOffer?.slogan && (
                                                                        <div className="text-[10px] text-slate-400 italic line-clamp-1">
                                                                            {matchingOffer.slogan}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Contract Settings */}
                            <div className="bg-slate-800/40 p-6 rounded-3xl border border-slate-700/50 space-y-4 shadow-xl">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Inicio de Contrato</label>
                                        <select
                                            name="inicio_contrato"
                                            value={formData.inicio_contrato}
                                            onChange={handleChange}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-100 outline-none focus:ring-2 focus:ring-primary/50 transition-all font-bold"
                                        >
                                            <option value="cuanto_antes">CUANTO ANTES</option>
                                            <option value="fecha_fija">FECHA FIJA</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className={`text-[9px] font-black uppercase tracking-widest ml-1 ${formData.inicio_contrato === 'fecha_fija' ? 'text-slate-300' : 'text-slate-600'}`}>Fecha Inicio</label>
                                        <input
                                            type="date"
                                            name="fecha_inicio"
                                            value={formData.fecha_inicio}
                                            onChange={handleChange}
                                            disabled={formData.inicio_contrato !== 'fecha_fija'}
                                            className={`w-full bg-slate-900 border ${!validateField('fecha_inicio', formData.fecha_inicio) ? 'border-rose-500' : 'border-slate-700'} rounded-xl px-3 py-2 text-xs text-slate-100 outline-none focus:ring-2 focus:ring-primary/50 transition-all disabled:opacity-30`}
                                        />
                                    </div>
                                </div>

                                {hasPermission('no_mandar_firma') && (
                                    <div className="pt-2">
                                        <SyncLockedCheckbox
                                            name="mandar_a_firma"
                                            label="Mandar contrato a firma"
                                            checked={formData.mandar_a_firma}
                                            onChange={handleChange}
                                            className="bg-slate-900/40"
                                        />
                                    </div>
                                )}

                                {!formData.mandar_a_firma && (
                                    <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-300">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Fecha de Firma Confirmada</label>
                                        <input
                                            type="date"
                                            name="fecha_firma_datos"
                                            value={formData.fecha_firma_datos || ''}
                                            onChange={handleChange}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-primary/50"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Main Actions */}
                            <div className="bg-slate-900/40 p-4 rounded-3xl border border-slate-700/30 space-y-3 shadow-2xl">
                                {scoringStatus === 'TIMEOUT' && (
                                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-3 animate-in fade-in zoom-in duration-300">
                                        <AlertCircle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-amber-400 uppercase tracking-tighter">Scoring no logrado (Timeout)</p>
                                            <p className="text-[10px] text-amber-500/80 leading-tight">
                                                Ha superado el tiempo de espera máximo de 1 minuto. Puedes forzar el envío pero requerirá revisión adicional.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className="flex"
                                    onMouseEnter={() => !isFormValid && setHighlightMissingFields(true)}
                                    onMouseLeave={() => setHighlightMissingFields(false)}
                                >
                                    <button
                                        type="submit"
                                        disabled={loading || formStatus === 'ENVIADO' || formData.contract_code || !isFormValid}
                                        className="w-full py-4 px-6 bg-primary hover:bg-primary/90 text-white text-sm font-black uppercase tracking-tighter rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-primary/30 disabled:opacity-50 disabled:grayscale"
                                    >
                                        {loading ? (
                                            <Loader2 size={24} className="animate-spin" />
                                        ) : (
                                            <>
                                                <span>Alta Contrato</span>
                                                <Send size={18} />
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="text-[10px] text-center text-slate-500 italic">Al confirmar el alta se enviará el contrato para su procesamiento y firma.</p>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            {/* Price Appendix */}
            <PriceAppendix
                isOpen={isAppendixOpen}
                onAnalysisComplete={(comparison) => {
                    setPriceData(prev => ({
                        ...prev,
                        comparison,
                        loadingComparison: false
                    }));
                }}
                onExtractionComplete={async (extracted) => {
                    if (analyzingFileId) {
                        try {
                            await authFetch(`/api/whatsapp/attachments/${analyzingFileId}/metadata`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ extraction_result: extracted })
                            });
                            setExtractedData(prev => ({
                                ...prev,
                                [analyzingFileId]: extracted
                            }));
                        } catch (err) {
                            console.error('[WhatsApp] Failed to save extraction metadata:', err);
                        } finally {
                            setAnalyzingFileId(null);
                        }
                    }
                }}
                fetchImaginaPrice={fetchImaginaPrice}
                onClose={() => setIsAppendixOpen(false)}
                loadingImagina={priceData.loadingImagina}
                loadingComparison={priceData.loadingComparison}
                imaginaData={priceData.imagina}
                comparisonData={priceData.comparison}
                tarifaName={selectedTarifa?.nombre}
                cups={formData.cups}
                idTarifa={formData.tarifa_id}
            />

            {/* Zoho Desk Tickets Appendix */}
            <ZohoDeskTicketsAppendix
                email={formData.email}
                isOpen={isZohoTicketsOpen}
                onClose={() => setIsZohoTicketsOpen(false)}
                authFetch={authFetch}
            />

            {/* Comparator Error Modal */}
            {comparatorError && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-slate-900 border border-slate-700/50 rounded-3xl p-8 max-w-sm w-full shadow-2xl flex flex-col items-center text-center scale-in-center overflow-hidden relative">
                        {/* A bit of red glow behind the icon */}
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-red-500/10 blur-2xl rounded-full pointer-events-none" />
        
                                <div className="relative p-4 bg-red-500/10 rounded-full mb-6 mt-2">
                                    <AlertCircle size={40} className="text-red-500 animate-pulse" />
                                </div>
        
                                <h3 className="text-xl font-bold text-slate-100 tracking-tight mb-3">Atención</h3>
        
                                <p className="text-sm text-slate-400 mb-8 whitespace-pre-wrap leading-relaxed max-w-[280px]">
                                    {comparatorError}
                                </p>

                        <button
                            type="button"
                            onClick={() => setComparatorError(null)}
                            className="w-full py-3.5 bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-slate-200 rounded-xl font-bold uppercase tracking-widest text-xs transition-colors border border-slate-700 hover:border-slate-500 shadow-lg"
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {/* Custom Reset Confirm Modal */}
            {isResetConfirmOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden scale-in-center">
                        <div className="p-6 text-center space-y-4">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto text-red-500">
                                <RotateCcw size={32} />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-lg font-bold text-slate-100">¿Limpiar formulario?</h3>
                                <p className="text-sm text-slate-400">
                                    Esta acción eliminará todos los datos introducidos. Úsalo solo si el cliente desea una oferta completamente diferente.
                                </p>
                            </div>
                        </div>
                        <div className="flex border-t border-slate-800">
                            <button
                                type="button"
                                onClick={() => setIsResetConfirmOpen(false)}
                                className="flex-1 px-4 py-4 text-sm font-bold text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all border-r border-slate-800"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={executeResetForm}
                                className="flex-1 px-4 py-4 text-sm font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
                            >
                                Sí, Limpiar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Loading Overlay for Conversation Switching */}
            {currentTramite?.status === 'LOADING' && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-slate-900/80 border border-slate-700/50 rounded-3xl p-10 max-w-sm w-full shadow-2xl flex flex-col items-center text-center scale-in-center">
                        <div className="relative mb-6">
                            <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full animate-pulse" />
                            <div className="relative p-5 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                                <Clock size={48} className="text-emerald-500 animate-[spin_3s_linear_infinite]" />
                            </div>
                            <Loader2 size={24} className="absolute -bottom-1 -right-1 text-emerald-400 animate-spin bg-slate-900 rounded-full p-1 border border-slate-800" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-100 tracking-tight mb-2">Abriendo Sesión</h3>
                        <p className="text-xs text-slate-400 leading-relaxed uppercase tracking-widest font-bold opacity-60">
                            Cargando datos del trámite...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesForm;
